/**
 * Hook para gerenciar Push Notifications
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// VAPID Public Key - será gerada pelo backend
const getVapidPublicKey = async () => {
  try {
    const response = await axios.get(`${API}/push/vapid-public-key`);
    return response.data.publicKey;
  } catch (error) {
    console.error('Erro ao obter VAPID key:', error);
    return null;
  }
};

// Converter base64 para Uint8Array (necessário para VAPID)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Verificar suporte ao carregar
  useEffect(() => {
    const checkSupport = () => {
      const supported = 
        'serviceWorker' in navigator && 
        'PushManager' in window &&
        'Notification' in window;
      
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
        checkExistingSubscription();
      }
    };
    
    checkSupport();
  }, []);

  // Verificar subscription existente
  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSub = await registration.pushManager.getSubscription();
      setSubscription(existingSub);
    } catch (err) {
      console.error('Erro ao verificar subscription:', err);
    }
  };

  // Registrar Service Worker
  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registrado:', registration);
      return registration;
    } catch (err) {
      console.error('Erro ao registrar Service Worker:', err);
      throw err;
    }
  };

  // Solicitar permissão e criar subscription
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications não suportadas neste navegador');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Solicitar permissão
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        setError('Permissão para notificações negada');
        return false;
      }

      // Registrar Service Worker
      await registerServiceWorker();
      const registration = await navigator.serviceWorker.ready;

      // Obter VAPID public key
      const vapidPublicKey = await getVapidPublicKey();
      if (!vapidPublicKey) {
        setError('Erro ao obter chave do servidor');
        return false;
      }

      // Criar subscription
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Enviar subscription para o backend
      await axios.post(`${API}/push/subscribe`, {
        subscription: sub.toJSON()
      });

      setSubscription(sub);
      return true;
    } catch (err) {
      console.error('Erro ao ativar notificações:', err);
      setError(err.message || 'Erro ao ativar notificações');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Cancelar subscription
  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;

    setIsLoading(true);
    setError(null);

    try {
      // Remover do backend
      await axios.post(`${API}/push/unsubscribe`, {
        endpoint: subscription.endpoint
      });

      // Cancelar subscription local
      await subscription.unsubscribe();
      setSubscription(null);
      return true;
    } catch (err) {
      console.error('Erro ao desativar notificações:', err);
      setError(err.message || 'Erro ao desativar notificações');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  // Testar notificação
  const sendTestNotification = useCallback(async () => {
    try {
      await axios.post(`${API}/push/test`);
      return true;
    } catch (err) {
      console.error('Erro ao enviar teste:', err);
      return false;
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed: !!subscription,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification
  };
}

export default usePushNotifications;
